import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { TelematicsProviderSource } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import useTelematicsFleets from '~hooks/useTelematicsFleets/useTelematicsFleets';
import { TelematicsVehicle } from '~hooks/useTelematicsVehicles/models';
import useTelematicsVehicles from '~hooks/useTelematicsVehicles/useTelematicsVehicles';
import {
  buildEquipmentMappingDef,
  equipmentIDDef,
  equipmentNameDef,
  lastSyncedDef,
  licensePlateDef,
  modelDef,
  RemoteVehicleRow,
  vinDef,
} from '~pages/Settings/Integrations/remoteVehicleDataGridColumnsDefinition';
import { ROUTES as routes } from '~router/routePaths';
import { Paginated } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface FleetManagementProps {
  providerSource: TelematicsProviderSource;
}

const NO_FLEET_PAGE: Paginated<TelematicsVehicle> = {
  data: [],
  pagination: { limit: DEFAULT_API_PAGE_SIZE, page: 1 },
};

export const FleetManagement = observer(({ providerSource }: FleetManagementProps) => {
  const { userStore } = useStores();

  const { getTelematicsVehicleByFleetId } = useTelematicsVehicles();
  const { getTelematicsFleets } = useTelematicsFleets();
  const { setSearchQueryValue, searchValue } = useDataGridSearch();
  const [isLoadingTelematicsFleets, setIsLoadingTelematicsFleets] = useState(false);
  const [fleetId, setFleetId] = useState('');

  const { goToPage, isFetching, pagination, reloadCurrentPage, rows, startOver } =
    useCursorPagination<TelematicsVehicle>({
      fetchPage: ({ cursor, limit, link, signal }) => {
        if (!fleetId) {
          return Promise.resolve(NO_FLEET_PAGE);
        }

        return getTelematicsVehicleByFleetId({
          cursor,
          fleetId,
          limit,
          link,
          searchQuery: searchValue,
          signal,
        });
      },
      limit: DEFAULT_API_PAGE_SIZE,
    });

  const handleEquipmentSaved = useCallback(() => {
    void reloadCurrentPage();
  }, [reloadCurrentPage]);

  const columns = useMemo(() => {
    return [
      equipmentIDDef,
      equipmentNameDef,
      modelDef,
      licensePlateDef,
      vinDef,
      lastSyncedDef,
      buildEquipmentMappingDef(handleEquipmentSaved),
    ];
  }, [handleEquipmentSaved]);

  const tableRows = useMemo<RemoteVehicleRow[]>(
    () => rows.map((vehicle) => ({ id: vehicle.id, remoteVehicle: vehicle })),
    [rows],
  );

  const companyId = getEffectiveUserCompanyId(userStore);

  useEffect(() => {
    setFleetId('');
    if (!companyId) {
      return;
    }

    // aborted when the company or provider changes, so a slower lookup for the old one is ignored
    const lookupController = new AbortController();
    const { signal } = lookupController;

    const fetchFleet = async () => {
      setIsLoadingTelematicsFleets(true);
      try {
        const fleets = (await getTelematicsFleets(signal)) ?? [];
        if (signal.aborted) {
          return;
        }

        const fleet = fleets.find(
          (f) => f.providerSource === providerSource && f.companyId === companyId,
        );

        setFleetId(fleet?.id ?? '');
      } catch {
        // useTelematicsFleets already reported the failure
      } finally {
        if (!signal.aborted) {
          setIsLoadingTelematicsFleets(false);
        }
      }
    };

    void fetchFleet();

    return () => lookupController.abort();
  }, [companyId, providerSource]);

  useEffect(() => {
    void startOver();
  }, [fleetId, searchValue, startOver]);

  const isLoading = isFetching || isLoadingTelematicsFleets || !companyId;

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        variant="text"
        color="secondary"
        component={Link}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
        startIcon={<ArrowBackIosNew fontSize="small" />}
      >
        {t('administration.integration.title')}
      </Button>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Typography variant="h5">
            {t(`administration.integration.${providerSource}.name`)}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 14,
          }}
        >
          {t(`administration.integration.${providerSource}.description`)}
        </Typography>
      </CardContent>
      <DataTable
        id={`${providerSource}-management-grid`}
        columns={columns}
        rows={tableRows}
        loading={isLoading}
        onSearchChange={setSearchQueryValue}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isLoading}
            onNavigate={(link) => void goToPage(link)}
            pagination={pagination}
          />
        }
      />
    </Box>
  );
});
